import Button from '@atlaskit/button';
import { cssMap } from '@atlaskit/css';
import AkModal from '@atlaskit/modal-dialog/modal-dialog';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { type App } from '../types';
import { portalZIndexFix } from '../utils/portalZIndexFix';
import { type JSX } from 'react/jsx-runtime';

const APP_REC_MODAL_CONTENT = 'app-rec-modal-content';

/*--Modal--*/

type ModalProps = {
	children: React.ReactNode;
	isOpen: boolean;
	onClose?: () => void;
	modalOpenCallback?: () => void;
};

const REASONS = [
	<FormattedMessage
		id="recommendation-controls.team-size"
		description="the first reason for recommending the product to the customer: the size of the customer's team"
		defaultMessage="the size of your team"
	/>,
	<FormattedMessage
		id="recommendation-controls.existing-products"
		description="the second reason for recommending the product to the customer: the customer's existing Atlassian products"
		defaultMessage="your existing Atlassian products"
	/>,
];

export const Modal = ({ children, isOpen, onClose }: ModalProps): JSX.Element => {
	portalZIndexFix(APP_REC_MODAL_CONTENT);

	return (
		<ModalTransition>
			{isOpen && (
				<AkModal shouldCloseOnEscapePress={true} onClose={onClose}>
					<div id={APP_REC_MODAL_CONTENT}>{children}</div>
				</AkModal>
			)}
		</ModalTransition>
	);
};

/*--Confirm Button--*/

type ConfirmButtonProps = {
	onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => unknown;
};

const AcceptButton = ({ onClick }: ConfirmButtonProps) => (
	<Button appearance="primary" onClick={onClick}>
		<FormattedMessage
			id="recommendations.ad-controls.reasons-modal.accept-button.label-ok"
			defaultMessage="OK"
			description="The text on the button that dismisses a modal which provides reasons an app has been recommended https://hello.atlassian.net/l/cp/ssoqm2Fq"
		/>
	</Button>
);

/*--Title--*/

const styles = cssMap({
	titleContainer: {
		font: token('font.heading.medium'),
	},
});

const Title = () => (
	<Box xcss={styles.titleContainer}>
		<FormattedMessage
			id="recommendations.ad-controls.reasons-modal.title"
			defaultMessage="Why am I seeing this?"
			description="The title for a modal the displays the reasons an app has been recommended. https://hello.atlassian.net/l/cp/ssoqm2Fq"
		/>
	</Box>
);

/*--Body--*/
const Body = (props: { productName: string }) => (
	<p>
		<FormattedMessage
			id="recommendations.ad-controls.reasons-modal.body"
			defaultMessage="We think that {productName} will improve your team's productivity. The recommendation is based on:"
			description="Explanation of why we think the recommended product will improve the customer's productivity."
			values={{
				productName: <strong>{props.productName}</strong>,
			}}
		/>
	</p>
);

/*--Reasons List--*/

type ReasonsListProps = { reasons: JSX.Element[] };

const reasonsListStyles = css({
	listStyleType: 'disc',
	paddingLeft: token('space.250', '20px'),
	marginTop: token('space.150', '12px'),
});

const ReasonsList = ({ reasons }: ReasonsListProps) => (
	<ul css={reasonsListStyles}>
		{reasons.map((reason, index) => (
			<li key={index}>{reason}</li>
		))}
	</ul>
);

/*--Privacy Policy Link--*/

type PrivacyPolicyLinkProps = {
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

const privacyPolicyLinkStyles = css({
	marginTop: token('space.150', '12px'),
});

const PrivacyPolicyLink = ({ onClick }: PrivacyPolicyLinkProps) => (
	<div css={privacyPolicyLinkStyles}>
		<a href={'https://www.atlassian.com/legal/privacy-policy'} target="_blank" onClick={onClick}>
			<FormattedMessage
				id="recommendations.ad-controls.reasons-modal.privacy-policy-link.text"
				defaultMessage="Learn more about your data privacy with Atlassian"
				description="The text linking to Atlassian's privacy policy. https://hello.atlassian.net/l/cp/ssoqm2Fq"
			/>
		</a>
	</div>
);

/*--Reason Modal--*/
type WhyAmISeeingThisModalProps = {
	isOpen: boolean;
	app: App;
	onClose: () => void;
};

const ReasonsModalBody = ({ app, onClose }: WhyAmISeeingThisModalProps) => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	const handleOnPrivacyPolicy = () => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'adControlsPrivacyPolicy',
		});
	};
	return (
		<>
			<ModalHeader>
				<ModalTitle>
					<Title />
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Body productName={app.appName} />
				<ReasonsList reasons={REASONS} />
				<PrivacyPolicyLink onClick={() => handleOnPrivacyPolicy()} />
			</ModalBody>
			<ModalFooter>
				<AcceptButton onClick={onClose} />
			</ModalFooter>
		</>
	);
};

export const WhyAmISeeingThisModal = (props: WhyAmISeeingThisModalProps): JSX.Element => {
	return (
		<Modal isOpen={props.isOpen}>
			<ReasonsModalBody {...props} />
		</Modal>
	);
};
